import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';
import type { Column } from 'write-excel-file/browser';
import type { Lender, LenderUser } from '../../api/lenders';
import { useLenderUsers } from '../../hooks/useLenders';
import { useUpdateUserStatus } from '../../hooks/useUsers';
import type { UserStatus } from '../../api/users';
import UserStatusDialog from '../users/UserStatusDialog';
import SuccessToast from '../../components/common/SuccessToast';
import TablePagination, { DEFAULT_TABLE_PAGE_SIZE } from '../../components/common/TablePagination';
import activeStatusIcon from './assets/lender-active-status.svg';
import inactiveStatusIcon from './assets/user-inactive-status.svg';
import importIcon from './assets/users-import.svg';
import plusIcon from './assets/product-plus.svg';
import Toast from '../../components/common/Toast';
import AddLenderUserDrawer from './AddLenderUserDrawer';
import EditLenderUserDrawer from './EditLenderUserDrawer';
import ImportLenderUsersDialog from './ImportLenderUsersDialog';
import { USER_ROLE_LABELS, type UserRole } from './wizard/types';
import searchIcon from './assets/product-search.svg';
import exportIcon from './assets/product-export.svg';
import exportActiveIcon from './assets/product-export-active.svg';
import filterIcon from './assets/product-filter.svg';
import sortIcon from './assets/product-sort.svg';
import dotsIcon from './assets/product-dots.svg';

const PAGE_SIZE = DEFAULT_TABLE_PAGE_SIZE;
const EXPORT_FAILED = 'Unable to export the users. Please try again.';

const ROLE_OPTIONS = Object.keys(USER_ROLE_LABELS) as UserRole[];
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const;

type StatusFilter = (typeof STATUS_OPTIONS)[number]['value'];
type SortKey = 'name' | 'email' | 'role' | 'products' | 'status' | 'lastActive';

type UserFilters = {
  roles: UserRole[];
  statuses: StatusFilter[];
};

const EMPTY_FILTERS: UserFilters = { roles: [], statuses: [] };

const fullName = (user: LenderUser): string => [user.firstName, user.lastName].filter(Boolean).join(' ');

const displayName = (user: LenderUser): string => fullName(user) || '—';

const roleLabel = (role: string | undefined): string => {
  if (!role) return '—';
  return role in USER_ROLE_LABELS ? USER_ROLE_LABELS[role as UserRole] : role;
};

export const isUserActive = (user: LenderUser): boolean => user.status !== 'blocked';

const lenderProductUrns = (lender: Lender): string[] =>
  (lender.products ?? []).map((product) => (typeof product === 'string' ? product : product.urn));

/**
 * "All" when the user inherits the lender's catalog, otherwise how many products it is scoped to.
 *
 * This used to compare the user's products against the catalog and call a complete match "All".
 * That is the drift OM-4022 is about: holding every product today is not the same as inheriting
 * the ones added tomorrow, and a restricted user who happens to hold them all read as inheriting.
 * The flag is stored now (OM-4614), so it is read rather than guessed.
 *
 * The count filters against the lender's active products: a user keeps products the lender later
 * removed, because their entries carry the thresholds of orders already placed.
 */
export const productAccessLabel = (user: LenderUser, lender: Lender): string => {
  if (user.inheritsProducts) return 'All';
  const active = new Set(lenderProductUrns(lender));
  return String((user.products ?? []).filter((product) => active.has(product.urn)).length);
};

const formatLastActive = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

/** The calendar day the table shows, at UTC midnight: the spreadsheet writer converts dates in UTC. */
const lastActiveDay = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

/** The table's columns; missing values stay empty instead of the table's dash. */
const exportColumns = (lender: Lender): Column<LenderUser>[] => [
  { header: 'Name', cell: (user) => fullName(user) || null, width: 24 },
  { header: 'Email', cell: (user) => user.email, width: 32 },
  { header: 'Role', cell: (user) => (user.role ? roleLabel(user.role) : null), width: 18 },
  { header: 'Products', cell: (user) => productAccessLabel(user, lender), width: 10 },
  { header: 'Status', cell: (user) => (isUserActive(user) ? 'Active' : 'Inactive'), width: 10 },
  { header: 'Last Active', cell: (user) => lastActiveDay(user.lastActive), width: 12 },
];

/** e.g. acme-users-2026-10-06.xlsx, dated by the viewer's calendar. */
const exportFileName = (lender: Lender): string => {
  const today = new Date();
  const day = [today.getFullYear(), today.getMonth() + 1, today.getDate()]
    .map((part) => String(part).padStart(2, '0'))
    .join('-');
  return `${lender.name}-users-${day}.xlsx`;
};

const sortValue = (user: LenderUser, key: SortKey, lender: Lender): string => {
  switch (key) {
    case 'name':
      return displayName(user).toLowerCase();
    case 'email':
      return user.email.toLowerCase();
    case 'role':
      return roleLabel(user.role).toLowerCase();
    case 'products':
      return productAccessLabel(user, lender).toLowerCase();
    case 'status':
      return isUserActive(user) ? 'active' : 'inactive';
    case 'lastActive':
      return user.lastActive ?? '';
  }
};

const matchesFilters = (user: LenderUser, filters: UserFilters): boolean => {
  if (filters.roles.length > 0 && (!user.role || !filters.roles.includes(user.role as UserRole))) {
    return false;
  }
  if (filters.statuses.length > 0) {
    const status: StatusFilter = isUserActive(user) ? 'active' : 'inactive';
    if (!filters.statuses.includes(status)) return false;
  }
  return true;
};

const toggleValue = <T extends string>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

function FilterMultiSelect<T extends string>({
  label,
  options,
  selected,
  onChange,
  open,
  onOpenChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (next: T[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const summary = selected.length === 0
    ? 'Select'
    : `${label} (${selected.length} Selected)`;

  return (
    <div>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => onOpenChange(!open)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-border-input bg-surface px-3 text-left text-sm text-content"
      >
        <span className={selected.length === 0 ? 'text-content-secondary' : undefined}>{summary}</span>
        <ChevronDown className="h-4 w-4 text-content-secondary" aria-hidden="true" />
      </button>
      {open && (
        <div role="listbox" aria-label={label} className="mt-1 max-h-40 overflow-y-auto rounded-md border border-border bg-surface py-1 shadow-sm">
          {options.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <label key={option.value} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-content hover:bg-surface-sunken">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onChange(toggleValue(selected, option.value))}
                  className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
                />
                {option.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function LenderUsersTab({ lender }: { lender: Lender }) {
  const { data: users = [], isLoading, isError } = useLenderUsers(lender.urn);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; direction: 1 | -1 } | null>(null);
  const [page, setPage] = useState(0);
  const [openMenu, setOpenMenu] = useState<{ user: LenderUser; top: number; right: number } | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [openFilterField, setOpenFilterField] = useState<'role' | 'status' | null>(null);
  const [draftFilters, setDraftFilters] = useState<UserFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<UserFilters>(EMPTY_FILTERS);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<LenderUser | null>(null);
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  const updateStatus = useUpdateUserStatus();
  const [statusChange, setStatusChange] = useState<{ user: LenderUser; status: UserStatus } | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusNotice, setStatusNotice] = useState<string | null>(null);
  const [selectedUserUrn, setSelectedUserUrn] = useState<string | null>(null);
  const submittingStatus = useRef(false);
  const actionButtons = useRef(new Map<string, HTMLButtonElement>());
  const menuRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const returnActionFocus = useCallback(() => {
    const trigger = selectedUserUrn ? actionButtons.current.get(selectedUserUrn) : undefined;
    (trigger ?? headingRef.current)?.focus();
  }, [selectedUserUrn]);

  const confirmStatus = async () => {
    if (!statusChange || submittingStatus.current) return;
    submittingStatus.current = true;
    setStatusError(null);
    try {
      await updateStatus.mutateAsync({ urn: statusChange.user.urn, status: statusChange.status });
      setStatusChange(null);
      setStatusNotice(statusChange.status === 'blocked' ? 'Success: User Deactivated' : 'Success: User Activated');
    } catch {
      setStatusError(`Unable to ${statusChange.status === 'blocked' ? 'deactivate' : 'activate'} this user. Please try again.`);
    } finally {
      submittingStatus.current = false;
    }
  };

  useEffect(() => {
    if (!openMenu) return;
    menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const close = () => setOpenMenu(null);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { close(); returnActionFocus(); }
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenu, returnActionFocus]);

  useEffect(() => {
    if (!filterOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (filterPanelRef.current?.contains(target) || filterButtonRef.current?.contains(target)) return;
      setFilterOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFilterOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [filterOpen]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = users.filter((user) => {
      if (!matchesFilters(user, appliedFilters)) return false;
      if (!term) return true;
      return [displayName(user), user.email, roleLabel(user.role), productAccessLabel(user, lender)]
        .some((value) => value.toLowerCase().includes(term));
    });
    if (!sort) return filtered;
    return [...filtered].sort((a, b) =>
      sort.direction * sortValue(a, sort.key, lender).localeCompare(sortValue(b, sort.key, lender)));
  }, [users, search, sort, lender, appliedFilters]);

  const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pagedRows = visibleRows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const hasAppliedFilters = appliedFilters.roles.length > 0 || appliedFilters.statuses.length > 0;

  const changeSort = (key: SortKey) => {
    setSort((current) => ({ key, direction: current?.key === key && current.direction === 1 ? -1 : 1 }));
    setPage(0);
  };

  /**
   * Editing a lender user happens in a drawer over this tab, not on the generic `/users/:urn`
   * page: role and product access are lender-scoped, and only `PATCH /lenders/:urn/users/:userUrn`
   * can write them (OM-4022, OM-4023). That page stays as it is for admin users.
   */
  const openProfile = (user: LenderUser) => {
    setEditing(user);
  };

  const openFilterPanel = () => {
    setDraftFilters({
      roles: [...appliedFilters.roles],
      statuses: [...appliedFilters.statuses],
    });
    setOpenFilterField(null);
    setFilterOpen((wasOpen) => !wasOpen);
  };

  const applyFilters = () => {
    setAppliedFilters({
      roles: [...draftFilters.roles],
      statuses: [...draftFilters.statuses],
    });
    setPage(0);
    setOpenFilterField(null);
    setFilterOpen(false);
  };


  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setOpenFilterField(null);
    setPage(0);
  };

  // Every user of the lender, whatever the search, filters, sort or page.
  const exportUsers = async () => {
    setExporting(true);
    setExportFailed(false);
    try {
      // Loaded on demand: only an export needs the spreadsheet writer.
      const { default: writeXlsxFile } = await import('write-excel-file/browser');
      await writeXlsxFile(users, {
        columns: exportColumns(lender),
        sheet: 'Users',
        dateFormat: 'mm/dd/yyyy',
        stickyRowsCount: 1,
      }).toFile(exportFileName(lender));
    } catch {
      setExportFailed(true);
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="rounded-xl border border-[#ddd] bg-surface p-6 text-content dark:border-border-subtle sm:p-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 ref={headingRef} tabIndex={-1} className="text-[18px] font-medium leading-[27px]">Users</h2>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setImporting(true)}
            className="flex h-[38px] items-center justify-center gap-2.5 rounded-lg border border-brand bg-surface px-4 text-[15px] font-semibold sm:w-[126px] text-content hover:bg-surface-sunken"
          >
            <img src={importIcon} alt="" />
            Import
          </button>
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex h-[38px] items-center justify-center gap-2.5 rounded-lg bg-brand px-4 text-[15px] font-semibold sm:w-[154px] text-white hover:bg-brand-hover"
          >
            <img src={plusIcon} alt="" />
            Add User
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <label className="flex h-[43px] w-full max-w-[408px] items-center gap-2 rounded-[5px] border border-border-input bg-surface px-3">
            <img src={searchIcon} alt="" />
            <span className="sr-only">Search lender users</span>
            <input
              type="search"
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(0); }}
              placeholder="Search ..."
              className="min-w-0 flex-1 bg-transparent text-sm text-content outline-none placeholder:text-content-secondary"
            />
          </label>
          <div className="relative flex items-center gap-2">
            <button
              type="button"
              aria-label="Export users"
              onClick={exportUsers}
              disabled={exporting || isLoading || users.length === 0}
              className="group rounded-[7px] bg-surface disabled:pointer-events-none disabled:opacity-60"
            >
              <img src={exportIcon} alt="" className="group-hover:hidden group-focus-visible:hidden" />
              <img src={exportActiveIcon} alt="" className="hidden group-hover:block group-focus-visible:block" />
            </button>
            <button
              ref={filterButtonRef}
              type="button"
              aria-label="Filter users"
              aria-expanded={filterOpen}
              aria-controls="lender-users-filter-panel"
              onClick={openFilterPanel}
              className={`rounded-[7px] bg-surface p-0.5${hasAppliedFilters || filterOpen ? ' ring-2 ring-brand' : ''}`}
            >
              <img src={filterIcon} alt="" />
            </button>

            {filterOpen && (
              <div
                ref={filterPanelRef}
                id="lender-users-filter-panel"
                role="dialog"
                aria-label="Filter"
                className="absolute right-0 top-[calc(100%+8px)] z-20 w-[280px] rounded-lg border border-border bg-surface p-4 shadow-lg"
              >
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-content">Filter</h3>
                  <button
                    type="button"
                    aria-label="Close filter"
                    onClick={() => { setFilterOpen(false); setOpenFilterField(null); }}
                    className="rounded p-0.5 text-content-secondary hover:bg-surface-sunken"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-content-secondary">Role</p>
                    <FilterMultiSelect
                      label="Role"
                      options={ROLE_OPTIONS.map((role) => ({ value: role, label: USER_ROLE_LABELS[role] }))}
                      selected={draftFilters.roles}
                      onChange={(roles) => setDraftFilters((current) => ({ ...current, roles }))}
                      open={openFilterField === 'role'}
                      onOpenChange={(open) => setOpenFilterField(open ? 'role' : null)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-content-secondary">Status</p>
                    <FilterMultiSelect
                      label="Status"
                      options={STATUS_OPTIONS.map((status) => ({ value: status.value, label: status.label }))}
                      selected={draftFilters.statuses}
                      onChange={(statuses) => setDraftFilters((current) => ({ ...current, statuses }))}
                      open={openFilterField === 'status'}
                      onOpenChange={(open) => setOpenFilterField(open ? 'status' : null)}
                    />
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-end gap-2">
                  <button type="button" onClick={resetFilters} className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-semibold text-content">
                    Reset
                  </button>
                  <button type="button" onClick={applyFilters} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white">
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {hasAppliedFilters && (
          <div className="flex flex-wrap items-center gap-2" aria-label="Applied filters">
            {appliedFilters.roles.length > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white">
                Role ({appliedFilters.roles.length} selected)
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            )}
            {appliedFilters.statuses.length > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white">
                Status ({appliedFilters.statuses.length} selected)
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            )}
            <button type="button" onClick={resetFilters} className="text-xs font-medium text-brand hover:underline">
              Reset to default
            </button>
          </div>
        )}

        {isError && <p role="alert" className="text-sm text-red-600">Users could not be loaded. Please try again.</p>}
        {exportFailed && <p role="alert" className="text-sm text-red-600">{EXPORT_FAILED}</p>}

        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[880px] table-fixed border-collapse text-left">
            <colgroup>
              <col /><col /><col className="w-[120px]" /><col className="w-[120px]" /><col className="w-[120px]" /><col className="w-[150px]" /><col className="w-[42px]" />
            </colgroup>
            <thead className="table-head text-white">
              <tr className="h-[42px]">
                {([
                  ['name', 'Name'],
                  ['email', 'Email'],
                  ['role', 'Role'],
                  ['products', 'Products'],
                  ['status', 'Status'],
                  ['lastActive', 'Last Active'],
                ] as [SortKey, string][]).map(([key, label]) => (
                  <th key={key} scope="col" className="px-4 text-xs font-semibold">
                    <button type="button" onClick={() => changeSort(key)} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      {label}<img src={sortIcon} alt="" />
                    </button>
                  </th>
                ))}
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="bg-surface text-[13px] leading-5 text-content-secondary">
              {isLoading ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center">Loading users...</td></tr>
              ) : pagedRows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center">No users found.</td></tr>
              ) : pagedRows.map((user) => {
                const name = displayName(user);
                const products = productAccessLabel(user, lender);
                const active = isUserActive(user);
                return (
                  <tr
                    key={user.urn}
                    tabIndex={0}
                    onClick={() => openProfile(user)}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openProfile(user);
                      }
                    }}
                    className={`h-11 cursor-pointer border-b border-gray-200 last:border-b-0 dark:border-border-subtle${selectedUserUrn === user.urn ? ' outline outline-2 -outline-offset-2 outline-brand-deep' : ''}`}
                  >
                    <td className="truncate px-4 text-content" title={name}>{name}</td>
                    <td className="truncate px-4" title={user.email}>{user.email}</td>
                    <td className="truncate px-4" title={roleLabel(user.role)}>{roleLabel(user.role)}</td>
                    <td className="truncate px-4">{products}</td>
                    <td className="px-4">
                      <span className="inline-flex items-center gap-1.5">
                        {active ? (
                          <>
                            <img src={activeStatusIcon} alt="" />
                            Active
                          </>
                        ) : (
                          <>
                            <img src={inactiveStatusIcon} alt="" />
                            Inactive
                          </>
                        )}
                      </span>
                    </td>
                    <td className="truncate px-4">{formatLastActive(user.lastActive)}</td>
                    <td className="text-center">
                      <button
                        type="button"
                        aria-label={`Actions for ${name}`}
                        aria-expanded={openMenu?.user.urn === user.urn}
                        aria-haspopup="menu"
                        ref={(element) => { if (element) actionButtons.current.set(user.urn, element); else actionButtons.current.delete(user.urn); }}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedUserUrn(user.urn);
                          const rect = event.currentTarget.getBoundingClientRect();
                          setOpenMenu((current) => current?.user.urn === user.urn
                            ? null
                            : { user, top: Math.max(8, Math.min(rect.bottom, window.innerHeight - 98)), right: Math.max(8, window.innerWidth - rect.right) });
                        }}
                        className="mx-auto flex h-7 w-5 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
                      >
                        <img src={dotsIcon} alt="" className="rotate-90" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <TablePagination totalRows={visibleRows.length} pageIndex={currentPage} pageSize={PAGE_SIZE}
          onPageChange={setPage} disabled={isLoading} />
      </div>

      {openMenu && createPortal(
        <>
          <button type="button" aria-label="Close user actions" className="fixed inset-0 z-40 cursor-default" onClick={() => { setOpenMenu(null); returnActionFocus(); }} />
          <div ref={menuRef} role="menu" aria-label={`Actions for ${fullName(openMenu.user) || openMenu.user.email}`}
            style={{ top: openMenu.top, right: openMenu.right }}
            onKeyDown={(event) => {
              const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
              const index = items.indexOf(document.activeElement as HTMLButtonElement);
              if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                event.preventDefault();
                const target = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
                  : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
                items[target]?.focus();
              }
              if (event.key === 'Tab') setOpenMenu(null);
            }}
            className="fixed z-50 w-[105px] overflow-hidden rounded-lg border border-[#e8ebf1] bg-white py-0.5 text-[15px] text-[#273141] shadow-[0_4px_4px_rgba(0,0,0,0.08)]">
            <div className="h-[42px] px-1 py-0.5">
              <button type="button" role="menuitem" onClick={() => { openProfile(openMenu.user); setOpenMenu(null); }}
                className="h-full w-full rounded border-[1.5px] border-transparent px-2 text-left leading-[22.5px] hover:bg-[#e8ebf1] hover:text-[#0f1b2f] focus-visible:bg-[#e8ebf1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">Edit</button>
            </div>
            <div className="h-[42px] px-1 py-0.5">
              <button type="button" role="menuitem" onClick={() => {
                setStatusError(null); setStatusNotice(null);
                setStatusChange({ user: openMenu.user, status: isUserActive(openMenu.user) ? 'blocked' : 'active' });
                setOpenMenu(null);
              }} className="h-full w-full rounded border-[1.5px] border-transparent px-2 text-left leading-[22.5px] hover:bg-[#e8ebf1] hover:text-[#0f1b2f] focus-visible:bg-[#e8ebf1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">
                {isUserActive(openMenu.user) ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}

      {adding && (
        <AddLenderUserDrawer
          lender={lender}
          onClose={() => setAdding(false)}
          onAdded={() => { setAdding(false); setNotice('Success: User Added'); }}
        />
      )}
      {editing && (
        <EditLenderUserDrawer
          lender={lender}
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setNotice('Success: User Updated'); }}
        />
      )}
      {importing && (
        <ImportLenderUsersDialog
          lender={lender}
          onClose={() => setImporting(false)}
          onImported={() => { setImporting(false); setNotice('Success: User List Imported'); }}
        />
      )}
      {statusChange && <UserStatusDialog userName={fullName(statusChange.user) || statusChange.user.email}
        status={statusChange.status} isPending={updateStatus.isPending} errorMessage={statusError}
        onConfirm={confirmStatus} onCancel={() => { if (!submittingStatus.current) { setStatusChange(null); setStatusError(null); } }}
        returnFocus={returnActionFocus} />}
      {statusNotice && <SuccessToast message={statusNotice} onDismiss={() => setStatusNotice(null)} />}
      {notice && <Toast message={notice} onClose={() => setNotice(null)} />}
    </section>
  );
}
